import { useEffect, useId, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'

import { LANGUAGES, useI18n } from '../lib/i18n.jsx'
import { useTheme } from '../lib/theme.jsx'
import { Container, IconButton, Logo, cx } from '../ui/index.js'
import { PUBLIC_NAV } from './routes.js'
import { useSite } from './useSiteData.js'

/**
 * Sticky site header.
 *
 * The whole header is one hairline: a 1px bottom border, no shadow and no
 * backdrop blur. Blur costs a compositor layer on every scroll frame and, on a
 * text-dense research page, mostly produces a smeared paragraph behind the nav.
 * The active navigation item is marked by a 2px accent underline that sits *on*
 * that border, so the header's silhouette never changes height as you navigate.
 *
 * The mobile affordance is a disclosure, not an off-canvas drawer: the same
 * `<nav>` element simply becomes a stacked panel below the breakpoint. One list
 * in the DOM means one tab order, no focus-trap machinery, and no chance of the
 * two copies drifting apart.
 *
 * 吸顶页头。
 *
 * 整个页头就是一条发丝线：1px 底边框，无投影、无背景模糊。模糊会在每一滚动帧上
 * 多出一个合成层，而在文字密集的科研页面上，它主要产出的是导航背后一段糊掉的正文。
 * 当前导航项用 2px 的强调色下划线标记，压在那条底边框「上面」，因此导航切换时
 * 页头的轮廓高度始终不变。
 *
 * 移动端用的是展开式菜单而不是抽屉：同一个 <nav> 元素在断点以下变成纵向面板。
 * DOM 里只有一份列表，就只有一条 Tab 顺序、不需要焦点陷阱，也不会出现两份副本
 * 各自走样。
 */

/** Glyph per colour mode. Decorative — the accessible name comes from t(). 各明暗模式的字形，装饰性的，可访问名称来自 t()。 */
const MODE_GLYPH = { light: '☀', dark: '☾', system: '◐' }

/** UI string key per colour mode. 各明暗模式对应的文案 key。 */
const MODE_LABEL_KEY = {
  light: 'common.themeLight',
  dark: 'common.themeDark',
  system: 'common.themeSystem',
}

/**
 * Header: wordmark, navigation, language toggle, theme toggle, mobile menu.
 *
 * 页头：站点标识、导航、语言切换、主题切换、移动端菜单。
 *
 * @returns {JSX.Element} Header / 页头。
 */
export default function SiteHeader() {
  const { t, pick, lang, toggle } = useI18n()
  const { mode, cycleMode } = useTheme()
  const { data: site } = useSite()
  const location = useLocation()
  const navId = useId()
  const [menuOpen, setMenuOpen] = useState(false)

  // Navigating with the menu open would leave it covering the page it just
  // took you to.
  // 菜单开着导航过去，它会盖在刚打开的页面上。
  useEffect(() => {
    setMenuOpen(false)
  }, [location.pathname])

  // The switch offers the language you are not reading, so its label is always
  // legible to the person who needs it.
  // 切换按钮提供的是「你没在读的那一种」语言，因此它的标签对需要它的人始终可读。
  const otherLanguage = LANGUAGES.find((entry) => entry.code !== lang)
  // Prefer the short form: a wordmark that wraps to two lines is not a wordmark.
  // 优先用缩写：会折成两行的站点标识就不算标识了。
  const wordmark = site ? site.groupShort || pick(site.groupName) : ''

  return (
    <header className="site-header">
      <Container className="site-header__inner">
        <Link className="site-header__wordmark" to="/">
          <Logo size="sm" />
          <span className="site-header__wordmark-text">{wordmark}</span>
        </Link>

        <IconButton
          className="site-header__disclosure"
          label={t('nav.menu')}
          aria-expanded={menuOpen}
          aria-controls={navId}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span aria-hidden="true">≡</span>
        </IconButton>

        <nav className={cx('site-header__nav', menuOpen && 'is-open')} id={navId} aria-label={t('nav.menu')}>
          {PUBLIC_NAV.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.exact}
              className={({ isActive }) => cx('site-header__link', isActive && 'is-active')}
            >
              {t(item.key)}
            </NavLink>
          ))}
        </nav>

        <div className="site-header__tools">
          <button
            type="button"
            className="site-header__lang"
            onClick={toggle}
            lang={otherLanguage.htmlLang}
            aria-label={t('common.language')}
          >
            {t('common.otherLanguage')}
          </button>
          <IconButton
            className="site-header__theme"
            label={t('common.themeToggle', { mode: t(MODE_LABEL_KEY[mode]) })}
            onClick={cycleMode}
          >
            <span aria-hidden="true">{MODE_GLYPH[mode]}</span>
          </IconButton>
        </div>
      </Container>
    </header>
  )
}
